import React, { Component } from 'react';
import shortid from 'shortid';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { isNil } from 'lodash';
import {
    isRunningInSEBBrowser,
    openSecureBrowser,
} from '@amplify/assessment-common/secure-exam-browser';
import AmplifyButton from '@amplify/amplify-button';
import classNames from 'classnames';
import {
    initPreventDoubleTapZoom,
    destroyPreventDoubleTapZoom,
} from './common/utils/preventDoubleTapZoom';
import HeaderContainer from './common/header/HeaderContainer';
import AvatarContainer from './common/avatar/avatarContainer';
import SvgSprite from './common/components/SvgSprite';
import SessionTrackerContainer from './common/user/SessionTrackerContainer';
import AccessibilitySettingsDropdown from './common/components/AccessibilitySettingsDropdown/AccessibilitySettingsDropdown';
import AmpNavigationContainer from './common/header/AmpNavigationContainer';
import {
    LANDING_PAGES,
    PAGES_WITH_MAIN_NAVIGATION_AND_SUBHEADER,
    STUDENT_ASSESSMENT,
} from './constants/url';
import LoadingPage from './common/components/LoadingPage';
import LogoutModalContainer from './common/logout/LogoutModalContainer';
import AppErrorContainer from './common/error/AppErrorContainer';
import AccessibilityErrorOverlay from './common/error/AccessibilityErrorOverlay';
import { SUPPORTED_LANGUAGES } from './constants/language';
import AudioSavingErrorContainer from './common/error/AudioSavingErrorContainer';
import Alex from './common/alex/Alex';
import DeviceRotateAnimation from './common/animation/DeviceRotateAnimation';
import Captions from './common/captions/Captions';
import ARGestureOverlay from './common/components/ARGestureOverlay';
import StaticAlexAvatar from './common/components/StaticAlexAvatar';
import { SEB_URL, STUDENT_HOME } from './constants/secureBrowser';
import '@amplify/amplify-styles/dist/assets/toolkit/styles-release/amp-styles.css';
import { setArPageTitle } from './trc/troUtils';
import keycloak from './common/keycloak/keycloak';
import { subscribeToRemoteLogout } from './common/keycloak/logoutBroadcast';
import { ApolloContext } from './ApolloContextProvider';
import {
    handleCloseSEB,
    isKioskChromeOS,
    storeKioskModeParameter,
    isChromeOS,
} from './common/utils/browserUtils';

const ALLOWED_CLASS_NAMES = [
    // Language codes
    'en',
    'eng',
    'english',
    'es',
    'spa',
    'spanish',
    // Font size classes
    'font-size-100',
    'font-size-125',
    'font-size-150',
    'font-size-200',
    // Application specific classes
    'ar-skin',
    'lantern-container',
    'extended-view',
    'mobile-view',
    'desktop-view',
];

const ALLOWED_FONT_SIZES = new Set([100, 150, 200]);
class App extends Component {
    static sanitizeClassName(value) {
        if (typeof value !== 'string' && typeof value !== 'number') return '';
        const stringValue = String(value)
            .toLowerCase()
            .trim();
        // Check if the value exists in our whitelist
        return ALLOWED_CLASS_NAMES.includes(stringValue) ? stringValue : '';
    }

    static isFontSizeValid(value) {
        if (typeof value !== 'number') return false;
        return ALLOWED_FONT_SIZES.has(value);
    }

    static isLanguageCodeValid(language) {
        if (typeof language !== 'string') return false;
        return SUPPORTED_LANGUAGES.includes(language);
    }

    constructor(props) {
        super(props);
        this.state = {
            startingSecureBrowser: false,
            showContent: true,
            contentKey: shortid.generate(),
            showGestureOverlay: false,
        };

        this.kioskStored = false;
        this.hasKioskError = false;
        this.kioskCheckComplete = false;
        this.setLanguageFromUrl();

        this.remountPageComponents = this.remountPageComponents.bind(this);
        this.handleGestureCaptured = this.handleGestureCaptured.bind(this);
        this.handleRemoteLogout = this.handleRemoteLogout.bind(this);
        this.remoteLogoutInProgress = false;
        this.unsubscribeRemoteLogout = null;
    }

    componentDidMount() {
        // Show gesture overlay for AR users who refresh mid-assessment
        // (but not on landing page, which already has interactive buttons)
        const isOnLandingPage = LANDING_PAGES.includes(this.props.location.pathname);
        if (
            this.props.isExternalURLSourcePresent &&
            !this.props.hasUserInteracted &&
            !isOnLandingPage
        ) {
            this.setState({ showGestureOverlay: true });
        }

        if (isOnLandingPage) {
            this.props.clearState();
        } else if (this.props.isLanternUser && !this.isAppDataFetched()) {
            this.props.fetchApplicationData();
        }

        this.unsubscribeRemoteLogout = subscribeToRemoteLogout(this.handleRemoteLogout);

        initPreventDoubleTapZoom();
        this.checkForSourceQueryParam();
        setArPageTitle(this.props.isExternalURLSourcePresent, 'Amplify Reading: Placement');
    }

    async componentDidUpdate(prevProps) {
        if (this.props.location.search !== prevProps.location.search) {
            this.setLanguageFromUrl();
        }

        if (this.hasAuthenticated(prevProps) && !this.isAppDataFetched()) {
            this.props.fetchApplicationData();
        }

        if (
            keycloak?.token &&
            isChromeOS() &&
            !this.kioskStored &&
            this.hasAuthenticated(prevProps)
        ) {
            this.kioskStored = true;
            const { isError } = await storeKioskModeParameter({
                token: keycloak?.token,
            });
            if (isError) {
                this.props.showVerifiedAccessKioskError();
                this.hasKioskError = true;
            }
            this.kioskCheckComplete = true;
            this.forceUpdate();
        }

        if (this.shouldStartSecureBrowser(prevProps)) {
            if (isChromeOS()) {
                if (this.kioskCheckComplete && !this.hasKioskError) {
                    this.props.showErrorInChromeOSWithoutKiosk();
                }
            } else {
                this.startSecureBrowser();
            }
        }
    }

    componentWillUnmount() {
        destroyPreventDoubleTapZoom();
        if (this.unsubscribeRemoteLogout) {
            this.unsubscribeRemoteLogout();
            this.unsubscribeRemoteLogout = null;
        }
    }

    handleRemoteLogout() {
        if (this.remoteLogoutInProgress) {
            return;
        }
        this.remoteLogoutInProgress = true;
        this.props.clearState();
        if (typeof keycloak.clearToken === 'function') {
            keycloak.clearToken();
        }
        window.location.assign(`${window.location.origin}${STUDENT_ASSESSMENT}`);
    }

    onFocusHandler() {
        // Should not allow student to pass assessments
        // decided to redirect to students home page
        sessionStorage.clear();
        window.location.assign(STUDENT_HOME);
    }

    startSecureBrowser() {
        this.setState({
            startingSecureBrowser: true,
        });
        window.addEventListener('focus', this.onFocusHandler);
        const { mazeSchoolId, currentSchoolyear } = this.props;
        // we use mazeSchoolId because this is only one measure which can be passed in SEB
        openSecureBrowser(
            `${SEB_URL}&school_id=${mazeSchoolId}&school_year=${currentSchoolyear}`,
            keycloak.token,
            keycloak.refreshToken,
        );
    }

    shouldStartSecureBrowser() {
        return (
            this.isAppDataFetched() &&
            this.props.hasSecureAssessments &&
            keycloak.token &&
            keycloak.refreshToken &&
            !this.state.startingSecureBrowser &&
            !isRunningInSEBBrowser() &&
            !isKioskChromeOS()
        );
    }

    handleGestureCaptured() {
        this.setState({ showGestureOverlay: false });
    }

    checkForSourceQueryParam() {
        const { source, benchmarkMode, benchmarkId, captions, arLanguage } = queryString.parse(
            this.props.location.search,
        );
        if (source === 'AR') {
            // Save initial query params for later use in redirects
            this.props.setInitialQueryParams(this.props.location.search);

            this.props.setExternalURLStatus();
            this.props.setBenchmarkModeStatus(benchmarkMode === 'true');
            this.props.setArLanguage(arLanguage);
        }
        if (!isNil(benchmarkId)) {
            this.props.setBenchmarkId(benchmarkId);
        }
        if (captions === 'true') {
            this.props.setShouldEnableCaptions();
        }
    }

    setLanguageFromUrl() {
        const { lang } = queryString.parse(this.props.location.search);
        // consider language in url only for assessment pages
        if (
            !LANDING_PAGES.includes(this.props.location.pathname) &&
            SUPPORTED_LANGUAGES.includes(lang)
        ) {
            this.props.setCurrentLanguage(lang);
        }
    }

    isAppDataFetched() {
        return this.props.isPlaylistDataFetched && this.props.isResultDataFetched;
    }

    hasAuthenticated(prevProps) {
        return !prevProps.isLanternUser && this.props.isLanternUser;
    }

    hasPlaylistContent() {
        return this.props.isPlaylistDataFetched;
    }

    gradeLabel() {
        const gradeLevelMap = {
            KG: 'K',
            PK: 'Pre-K',
        };
        return this.props.studentGrade in gradeLevelMap
            ? gradeLevelMap[this.props.studentGrade]
            : parseInt(this.props.studentGrade, 10).toString();
    }

    headerTitle() {
        return `${this.props.studentName} - Grade ${this.gradeLabel()}`;
    }

    remountPageComponents() {
        this.setState({
            showContent: false,
            contentKey: shortid.generate(),
        });
        // Using setTimeout to add a delay before showing the content again.
        // This gives a chance for the children components to finish clearing the state.
        setTimeout(() => {
            this.setState({
                showContent: true,
            });
        }, 200);
    }

    shouldShowMainNavigationAndSubheader() {
        return PAGES_WITH_MAIN_NAVIGATION_AND_SUBHEADER.includes(this.props.location.pathname);
    }
    /*
        If we are at SEB -- show specific header with name only.
        If the logout button should be visible, the global navigation should be
        shown. Otherwise, the Lantern header appears instead.
    */

    displayNavigation() {
        if (this.shouldShowMainNavigationAndSubheader()) {
            return (
                <div
                    className={classNames('navigation-container', {
                        'hide-all-nav-buttons': isRunningInSEBBrowser() || isKioskChromeOS(),
                    })}
                >
                    <AmpNavigationContainer trackLogOut={this.props.trackLogOut} />
                    <div className="subheader">
                        <div className="mclass-logo">
                            <span>mCLASS&#174;</span>
                        </div>
                        <div className="buttons-container">
                            <AccessibilitySettingsDropdown />
                            {isRunningInSEBBrowser() && (
                                <AmplifyButton
                                    oval
                                    variant="secondary"
                                    className="close-button"
                                    onClick={handleCloseSEB}
                                    icon="icon-v2-close"
                                >
                                    Close
                                </AmplifyButton>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <React.Fragment>
                <HeaderContainer
                    studentName={this.props.studentName}
                    studentGrade={this.gradeLabel()}
                    location={this.props.location}
                />
            </React.Fragment>
        );
    }

    renderAvatar() {
        if (this.props.isExternalURLSourcePresent) {
            return <Alex avatarConfig={{ animateAlex: true }} />;
        }
        return <AvatarContainer />;
    }

    pageContent() {
        return (
            <div>
                {this.hasPlaylistContent() && this.state.showContent ? (
                    <div key={this.state.contentKey}>
                        {!this.props.isExternalURLSourcePresent && this.displayNavigation()}
                        {this.isAppDataFetched() && this.renderAvatar()}
                        {this.props.isResultDataFetched ? (
                            this.props.isAvatarReadyToAnimate && this.props.children
                        ) : (
                            <LoadingPage />
                        )}
                        {this.props.captionsEnabled && <Captions />}
                        <LogoutModalContainer />
                        <SvgSprite />
                    </div>
                ) : (
                    <LoadingPage />
                )}
            </div>
        );
    }

    render() {
        const safeLanguageClass = App.isLanguageCodeValid(this.props.currentLanguage)
            ? App.sanitizeClassName(this.props.currentLanguage)
            : '';
        const safeFontSizeClass =
            this.context &&
            this.context.fontSizeScaleValue &&
            App.isFontSizeValid(this.context.fontSizeScaleValue)
                ? App.sanitizeClassName(`font-size-${this.context.fontSizeScaleValue}`)
                : '';

        // Show gesture overlay for AR users who need to unlock audio
        if (this.state.showGestureOverlay) {
            return (
                <div className="lantern-container ar-skin">
                    <StaticAlexAvatar />
                    <ARGestureOverlay onGestureCaptured={this.handleGestureCaptured} />
                    <SvgSprite />
                </div>
            );
        }

        return this.state.startingSecureBrowser ? (
            <LoadingPage />
        ) : (
            <div
                className={classNames('lantern-container', {
                    'ar-skin': this.props.isExternalURLSourcePresent,
                    [safeLanguageClass]: safeLanguageClass,
                    [safeFontSizeClass]: safeFontSizeClass,
                })}
            >
                {this.props.isExternalURLSourcePresent && <DeviceRotateAnimation />}
                {this.props.isLanternUser ? this.pageContent() : <SessionTrackerContainer />}
                <AppErrorContainer pageReloadCallback={this.remountPageComponents} />
                <AudioSavingErrorContainer />
                {this.props.isLanternUser && <AccessibilityErrorOverlay />}
            </div>
        );
    }
}

App.contextType = ApolloContext;

App.propTypes = {
    isPlaylistDataFetched: PropTypes.bool,
    isResultDataFetched: PropTypes.bool,
    children: PropTypes.node,
    studentName: PropTypes.string,
    studentGrade: PropTypes.string,
    isLanternUser: PropTypes.bool,
    isAvatarReadyToAnimate: PropTypes.bool.isRequired,
    fetchApplicationData: PropTypes.func.isRequired,
    clearState: PropTypes.func,
    location: PropTypes.object.isRequired,
    setCurrentLanguage: PropTypes.func,
    currentLanguage: PropTypes.string,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    setExternalURLStatus: PropTypes.func.isRequired,
    setInitialQueryParams: PropTypes.func.isRequired,
    setArLanguage: PropTypes.func.isRequired,
    setBenchmarkModeStatus: PropTypes.func.isRequired,
    trackLogOut: PropTypes.func,
    setBenchmarkId: PropTypes.func.isRequired,
    setShouldEnableCaptions: PropTypes.func.isRequired,
    captionsEnabled: PropTypes.bool.isRequired,
    hasSecureAssessments: PropTypes.bool.isRequired,
    currentSchoolyear: PropTypes.number,
    showErrorInChromeOSWithoutKiosk: PropTypes.func,
    showVerifiedAccessKioskError: PropTypes.func,
    mazeSchoolId: PropTypes.string,
    hasUserInteracted: PropTypes.bool,
};

App.defaultProps = {
    isPlaylistDataFetched: false,
    isResultDataFetched: false,
    children: undefined,
    studentName: '',
    studentGrade: '',
    isLanternUser: false,
    clearState: () => {},
    setCurrentLanguage: () => {},
    currentLanguage: '',
    trackLogOut: () => {},
    currentSchoolyear: 2024,
    showErrorInChromeOSWithoutKiosk: () => {},
    showVerifiedAccessKioskError: () => {},
    mazeSchoolId: '',
    hasUserInteracted: false,
};

export default App;
